import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { adminAnalyticsService, type RevenueAnalytics } from '../services/adminAnalyticsService'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

const AdminAnalyticsPage = () => {
  const navigate = useNavigate()
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d')
  const [revenueData, setRevenueData] = useState<RevenueAnalytics[]>([])
  const [systemMetrics, setSystemMetrics] = useState<any>(null)
  const [topUsers, setTopUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAnalyticsData()
  }, [timeRange])

  const loadAnalyticsData = async () => {
    try {
      setLoading(true)
      const [revenue, metrics, users] = await Promise.all([
        adminAnalyticsService.getRevenueAnalytics(timeRange),
        adminAnalyticsService.getSystemMetrics(),
        adminAnalyticsService.getTopUsers(10)
      ])
      
      setRevenueData(revenue)
      setSystemMetrics(metrics)
      setTopUsers(users)
    } catch (error) {
      console.error('Error loading analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  const totalRevenue = revenueData.reduce((sum, day) => sum + day.revenue, 0)
  const totalSubscriptions = revenueData.reduce((sum, day) => sum + day.subscriptions, 0)
  const totalUpgrades = revenueData.reduce((sum, day) => sum + day.upgrades, 0)
  const totalCancellations = revenueData.reduce((sum, day) => sum + day.cancellations, 0)


  const conversionRate = totalSubscriptions > 0 ? (totalUpgrades / totalSubscriptions) * 100 : 0
  const churnRate = totalSubscriptions > 0 ? (totalCancellations / totalSubscriptions) * 100 : 0

  const maxRevenue = Math.max(...revenueData.map(d => d.revenue))

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: `$${totalRevenue.toLocaleString()}`,
      change: 12.5,
      icon: CurrencyDollarIcon,
      color: 'text-green-400'
    },
    {
      title: 'New Subscriptions',
      value: totalSubscriptions.toString(),
      change: 8.2,
      icon: UserGroupIcon,
      color: 'text-blue-400'
    },
    {
      title: 'Conversion Rate',
      value: `${conversionRate.toFixed(1)}%`,
      change: 3.1,
      icon: ArrowTrendingUpIcon,
      color: 'text-purple-400'
    },
    {
      title: 'Churn Rate',
      value: `${churnRate.toFixed(1)}%`,
      change: -2.4,
      icon: ArrowTrendingDownIcon,
      color: 'text-red-400'
    }
  ]

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader title="Analytics & Reports" subtitle="Detailed business insights and metrics">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="text-red-400 text-sm font-medium">Admin Panel</span>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            Admin Dashboard
          </button>
          <ProfileDropdown />
        </div>
      </AppHeader>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Time Range Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-8"
        >
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Analytics Dashboard</h1>
            <p className="text-gray-400">Track performance and business metrics</p>
          </div>
          <div className="flex items-center space-x-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  timeRange === range
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/10 text-gray-400 hover:text-white hover:bg-white/20'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </motion.div>

        {/* KPI Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {kpiCards.map((kpi, index) => (
            <motion.div
              key={kpi.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="glass-mockup rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <kpi.icon className={`w-8 h-8 ${kpi.color}`} />
                <div className={`flex items-center space-x-1 ${kpi.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {kpi.change >= 0 ? (
                    <ArrowTrendingUpIcon className="w-4 h-4" />
                  ) : (
                    <ArrowTrendingDownIcon className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">{Math.abs(kpi.change)}%</span>
                </div>
              </div>
              <div className="text-2xl font-bold text-white mb-1">{kpi.value}</div>
              <div className="text-gray-400 text-sm">{kpi.title}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Revenue Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-6">Revenue Trend</h3>
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <div className="h-64 flex items-end space-x-2">
                {revenueData.map((day) => (
                  <div
                    key={day.date}
                    className="flex-1 flex flex-col items-center group"
                  >
                    <div className="relative w-full">
                      <div
                        className="w-full bg-gradient-to-t from-blue-500 to-purple-500 rounded-t-lg transition-all duration-300 group-hover:from-blue-400 group-hover:to-purple-400"
                        style={{
                          height: `${(day.revenue / maxRevenue) * 200}px`,
                          minHeight: '4px'
                        }}
                      ></div>
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        ${day.revenue.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-2 transform rotate-45 origin-left">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Subscription Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-6">Subscription Metrics</h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">New Subscriptions</div>
                  <div className="text-gray-400 text-sm">This period</div>
                </div>
                <div className="text-2xl font-bold text-blue-400">{totalSubscriptions}</div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">Upgrades</div>
                  <div className="text-gray-400 text-sm">Plan upgrades</div>
                </div>
                <div className="text-2xl font-bold text-green-400">{totalUpgrades}</div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">Cancellations</div>
                  <div className="text-gray-400 text-sm">Subscription cancellations</div>
                </div>
                <div className="text-2xl font-bold text-red-400">{totalCancellations}</div>
              </div>
              
              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Net Growth</div>
                    <div className="text-gray-400 text-sm">Subscriptions - Cancellations</div>
                  </div>
                  <div className="text-2xl font-bold text-purple-400">
                    {totalSubscriptions - totalCancellations}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-6">Top Users by Activity</h3>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-gray-400 text-sm">Loading users...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {topUsers.map((user, index) => (
                  <div key={user.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                        {index + 1}
                      </div>
                      <div>
                        <div className="text-white font-medium">{user.first_name} {user.last_name}</div>
                        <div className="text-gray-400 text-sm">{user.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-bold">{user.shootCount}</div>
                      <div className="text-gray-400 text-sm">shoots</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* System Performance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-mockup rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-6">System Performance</h3>
            {systemMetrics ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-white">Database Size</span>
                  </div>
                  <span className="text-gray-400">{systemMetrics.databaseSize}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-white">API Response Time</span>
                  </div>
                  <span className="text-gray-400">{systemMetrics.apiResponseTime}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-white">Uptime</span>
                  </div>
                  <span className="text-gray-400">{systemMetrics.uptime}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-white">Active Connections</span>
                  </div>
                  <span className="text-gray-400">{systemMetrics.activeConnections}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-white">Error Rate</span>
                  </div>
                  <span className="text-gray-400">{systemMetrics.errorRate}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="w-4 h-4 text-blue-400" />
                    <span className="text-white">Last Backup</span>
                  </div>
                  <span className="text-gray-400">
                    {new Date(systemMetrics.lastBackup).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-gray-400 text-sm">Loading metrics...</p>
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  )
}

export default AdminAnalyticsPage 